import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Paper
} from '@mui/material';
import { Build as EquipmentIcon } from '@mui/icons-material';
import { surgicalEquipmentService } from '../../services/surgicalEquipmentService';

const EquipmentMultiSelect = ({ 
  facilityId, 
  value = [], 
  onChange, 
  disabled = false,
  size = "small",
  fullWidth = true,
  label = "Equipment Needed",
  placeholder = "Search and select equipment...",
  ...otherProps 
}) => {
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadEquipment = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const equipment = await surgicalEquipmentService.getAllEquipment(facilityId);
      // Filter only operational equipment for selection
      const operationalEquipment = equipment.filter(item => item.status === 'operational');
      setEquipmentOptions(operationalEquipment);
    } catch (err) {
      console.error('Error loading equipment:', err);
      setError('Failed to load equipment');
      setEquipmentOptions([]);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  // Load equipment when facilityId changes
  useEffect(() => {
    if (facilityId) {
      loadEquipment();
    } else {
      setEquipmentOptions([]);
    }
  }, [facilityId, loadEquipment]);

  // Convert value array of IDs to equipment objects for display
  const selectedEquipment = value ? equipmentOptions.filter(equipment => 
    value.includes(equipment.id)
  ) : [];

  const handleChange = (event, newValue) => {
    // Convert selected equipment objects back to array of IDs
    const selectedIds = newValue.map(equipment => equipment.id);
    onChange(selectedIds);
  };

  const getOptionLabel = (option) => {
    if (typeof option === 'string') {
      // Handle case where option is just an ID (shouldn't happen normally)
      const equipment = equipmentOptions.find(eq => eq.id === option);
      return equipment ? `${equipment.name} (${equipment.type})` : option;
    }
    return `${option.name} (${option.type})`;
  };

  const renderOption = (props, option) => (
    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
      <EquipmentIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {option.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {option.type} • {option.location} • S/N: {option.serialNumber}
        </Typography>
      </Box>
    </Box>
  );

  const renderTags = (tagValue, getTagProps) =>
    tagValue.map((option, index) => (
      <Chip
        variant="outlined"
        label={option.name}
        size="small"
        {...getTagProps({ index })}
        key={option.id}
        icon={<EquipmentIcon />}
        sx={{ 
          maxWidth: 200,
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }
        }}
      />
    ));

  if (error) {
    return (
      <TextField
        size={size}
        fullWidth={fullWidth}
        label={label}
        value=""
        disabled
        error
        helperText={error}
        {...otherProps}
      />
    );
  }

  return (
    <Autocomplete
      multiple
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      options={equipmentOptions}
      value={selectedEquipment}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderTags={renderTags}
      filterOptions={(options, { inputValue }) => {
        if (!inputValue) return options;
        const filterValue = inputValue.toLowerCase();
        return options.filter(option => 
          option.name.toLowerCase().includes(filterValue) ||
          option.type.toLowerCase().includes(filterValue) ||
          option.manufacturer.toLowerCase().includes(filterValue) ||
          option.serialNumber.toLowerCase().includes(filterValue) ||
          option.location.toLowerCase().includes(filterValue)
        );
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={
        loading ? "Loading equipment..." : 
        equipmentOptions.length === 0 ? "No equipment available" : 
        "No matching equipment found"
      }
      PaperComponent={(props) => (
        <Paper {...props} sx={{ maxHeight: 300, overflow: 'auto' }} />
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedEquipment.length === 0 ? placeholder : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          {...otherProps}
        />
      )}
    />
  );
};

export default EquipmentMultiSelect; 